import { useState } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Check, Circle, Eye, MessageSquareReply, MinusCircle, Pencil, Plus, Send } from 'lucide-react';
import type {
  CursorPage, Industry, MedicalConfigChangeRequest,
  MedicalConfigurationRow, OnboardingStatus, Plant, PlantDetail, SetupMode, SetupStatus, SetupStepKey, WorkforceBand,
} from '@dru/types';
import { BRAND, SETUP_MODE_LABEL, SETUP_STATUS_LABEL, USER_STATUS_LABEL, WORKFORCE_BANDS } from '@dru/types';
import {
  addressForm, addressPayload, Alert, Button, ConfirmDialog, DataTable, DetailSection, Dialog,
  Field, IconAction, InfoTip, RowActions, Select, SelectField, StatusBadge, Textarea, TextField, useIdempotencyKey,
  type AddressFormValue,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { AddressFields } from '../../../components/AddressFields';
import { IndustryCombobox, industryFromRef } from '../../../components/IndustryCombobox';
import { Builder, Detail } from '../../../components/MedicalConfigBuilder';
import { SignInAsAction, useSignInAs } from './ImpersonateDialog';
import { holds } from '../../../layouts/ConsoleShell';
import { PlantAccessField } from '../../../components/PlantAccessField';
import { initialPlantAccess, plantAccessMode, plantAccessReady, scopeIdsFor } from '../../../lib/plant-access';

/**
 * Support-assisted setup, from the company page.
 *
 * Everything here goes through `/admin/tenants/:id/setup/*`, which binds the
 * company, audits the act, and calls the same services the company's own HR
 * console calls. The screen is organised the way the wizard is, so a support
 * engineer and the customer's HR are looking at the same eight steps and can
 * say "step three is done" to each other.
 */
export type SetupSection = 'overview' | 'structure' | 'users' | 'medical' | 'requests';
type Section = SetupSection;

/**
 * Each part of setup is its own tab on the company page (`?tab=plants`), not
 * a second row of buttons inside Configuration.
 */
export const SETUP_SECTION_TAB: Record<SetupSection, string> = {
  overview: 'setup', structure: 'plants', users: 'hr-users', medical: 'medical', requests: 'requests',
};

export function CompanySetup({ tenantId, industry, onChanged, view }: {
  tenantId: string;
  industry: { id: string; code: string; name: string } | null;
  /** Told whenever setup changes, so the company header's figures follow. */
  onChanged?: () => void;
  /** Which part of setup this tab shows. */
  view: SetupSection;
}) {
  const base = `/admin/tenants/${tenantId}/setup`;
  const { permissions } = useAuth();
  const fullSetup = holds(permissions, 'tenant.setup');
  const status = useQuery<OnboardingStatus>(() => api.get(base), [base]);
  // A step's button ("Manage plants", "Add HR user") opens that part's tab.
  const [params, setParams] = useSearchParams();
  const section: Section = fullSetup || view === 'overview' || view === 'structure' ? view : 'overview';
  const setSection = (next: Section) => {
    const p = new URLSearchParams(params);
    p.set('tab', SETUP_SECTION_TAB[next]);
    p.delete('section');
    setParams(p, { replace: true });
  };
  const reload = () => { status.reload(); onChanged?.(); };
  const [activeDialog, setActiveDialog] = useState<null | 'profile' | 'location'>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const s = status.data;

  async function setStatus(next: SetupStatus, mode?: SetupMode) {
    setBusy(true); setError(null);
    try {
      await api.post(`${base}/status`, { status: next, ...(mode ? { setupMode: mode } : {}) });
      reload();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  async function update(patch: Record<string, unknown>) {
    setBusy(true); setError(null);
    try { await api.patch(base, patch); reload(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  async function mark(step: string, state: 'done' | 'skipped' | 'clear') {
    setBusy(true); setError(null);
    try { await api.post(`${base}/steps/${step}`, { state }); reload(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  function renderStepAction(key: SetupStepKey, isActuallyDone: boolean) {
    if (!fullSetup && key !== 'plants') return null;
    switch (key) {
      case 'profile':
        return (
          <Button
            size="sm"
            variant={isActuallyDone ? 'secondary' : 'primary'}
            onClick={() => setActiveDialog('profile')}
          >
            {isActuallyDone ? 'Edit profile' : 'Complete profile'}
          </Button>
        );
      case 'location':
        return (
          <Button
            size="sm"
            variant={isActuallyDone ? 'secondary' : 'primary'}
            onClick={() => setActiveDialog('location')}
          >
            {isActuallyDone ? 'Edit location' : 'Add address'}
          </Button>
        );
      case 'plants':
        return (
          <Button
            size="sm"
            variant={isActuallyDone ? 'secondary' : 'primary'}
            onClick={() => setSection('structure')}
          >
            {isActuallyDone ? 'Manage plants' : 'Add plant'}
          </Button>
        );
      // The workforce is the company's own record. Seeing it is privileged
      // access; adding to it is done as the company's HR, from HR users.
      case 'employees':
        return isActuallyDone
          ? holds(permissions, 'crosstenant.employee.read') ? (
            <Link
              to={`/admin/companies/${tenantId}/oversight?tab=employees`}
              className="inline-flex h-8 max-md:h-10 items-center rounded-btn border border-neutral-300 bg-white px-3 text-small font-semibold text-neutral-800 hover:bg-neutral-50"
            >
              View workforce…
            </Link>
          ) : null
          // Importing is done signed in as one of the company's HR users,
          // from HR users; the button says where it goes (flow audit 2.7).
          : (
            <Button size="sm" variant="secondary" onClick={() => setSection('users')}>
              Open Users
            </Button>
          );
      case 'users':
        return (
          <Button
            size="sm"
            variant={isActuallyDone ? 'secondary' : 'primary'}
            onClick={() => setSection('users')}
          >
            {isActuallyDone ? 'Manage users' : 'Add HR user'}
          </Button>
        );
      case 'medical':
        return (
          <Button
            size="sm"
            variant={isActuallyDone ? 'secondary' : 'primary'}
            onClick={() => setSection('medical')}
          >
            {isActuallyDone ? 'Manage config' : 'Configure tests'}
          </Button>
        );
      // The one completion action (flow audit 2.1): enabled once the
      // required steps are done, and the way back out once it is finished.
      case 'review':
        return s?.setupStatus === 'COMPLETED' ? (
          <Button size="sm" variant="secondary" onClick={() => setStatus('IN_PROGRESS')} disabled={busy}>
            Reopen setup
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => setStatus('COMPLETED')}
            disabled={busy || (s?.missingRequired.length ?? 0) > 0}
            title={s?.missingRequired.length ? `Finish first: ${s.missingRequired.join(', ')}` : undefined}
          >
            Finish setup
          </Button>
        );
      default:
        return null;
    }
  }

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger">{error}</Alert>}
      {status.error && <Alert tone="danger">{status.error}</Alert>}
      {s && section === 'overview' && <IndustryCombobox label="Industry Type (optional)" value={industryFromRef(industry)} onChange={async selected => {
        try { await api.patch(`${base}/industry`, { industryId: selected?.id ?? null }); reload(); }
        catch (e) { setError(errorMessage(e)); }
      }} info="Explicit selection only." />}


      {section === 'overview' && s && (
        <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
          <DetailSection
            title={`Configuration progress · ${s.progress.completed} of ${s.progress.total}`}
          >
            <ol className="divide-y divide-neutral-100">
              {s.steps.map((st) => {
                const isActuallyDone = Boolean(st.actuallyDone);
                const isManuallyMarked = st.done && !isActuallyDone;

                return (
                  <li key={st.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <span className="mt-0.5 shrink-0">
                        {isActuallyDone ? (
                          <Check className="h-4 w-4 text-success-600" />
                        ) : isManuallyMarked ? (
                          <AlertTriangle className="h-4 w-4 text-warning-600" />
                        ) : st.skipped ? (
                          <MinusCircle className="h-4 w-4 text-neutral-400" />
                        ) : (
                          <Circle className="h-4 w-4 text-neutral-300" />
                        )}
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="flex items-center gap-1">
                            <span className="text-small font-semibold text-neutral-900">
                              {st.index}. {st.title}
                            </span>
                            {st.hint && <InfoTip label={`About ${st.title}`}>{st.hint}</InfoTip>}
                          </span>
                          {isActuallyDone ? (
                            <StatusBadge tone="covered">Completed</StatusBadge>
                          ) : isManuallyMarked ? (
                            <StatusBadge tone="dueSoon">Marked (missing data)</StatusBadge>
                          ) : st.skipped ? (
                            <StatusBadge tone="neutral">Skipped</StatusBadge>
                          ) : st.required ? (
                            <StatusBadge tone="pending">Required</StatusBadge>
                          ) : (
                            <StatusBadge tone="neutral">Optional</StatusBadge>
                          )}
                        </div>

                        {st.detail && (
                          <div className="mt-0.5 text-caption text-neutral-600">
                            {st.detail}
                          </div>
                        )}

                        {!isActuallyDone && st.missing && st.missing.length > 0 && (
                          <div className="mt-1 flex items-center gap-1 text-caption font-medium text-warning-600">
                            <span>Missing:</span>
                            <span className="font-normal">{st.missing.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center sm:justify-end gap-2 shrink-0">
                      {/* Primary Direct Action to Complete/Manage */}
                      {renderStepAction(st.key, isActuallyDone)}

                      {/* Optional steps can be skipped. A required step is done
                          when its data exists and only then, so it offers no
                          "Mark done" that would bypass that (flow audit 2.1). */}
                      {fullSetup && !st.required && st.key !== 'review' && (
                        <div className="flex items-center gap-1 text-caption">
                          {!st.done && !st.skipped && (
                            <button type="button" onClick={() => mark(st.key, 'skipped')} disabled={busy} className={STEP_LINK}>
                              Skip
                            </button>
                          )}
                          {(st.skipped || isManuallyMarked) && (
                            <button type="button" onClick={() => mark(st.key, 'clear')} disabled={busy} className={STEP_LINK}>
                              Reset
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </DetailSection>

          <div className="space-y-4">
            <DetailSection title="Mode and status">
              <div className="space-y-3">
                <SelectField label="Who sets this company up?" value={s.setupMode}
                  onChange={(v) => update({ setupMode: v })} disabled={busy || !fullSetup}>
                  {(Object.keys(SETUP_MODE_LABEL) as SetupMode[]).map((m) => (
                    <option key={m} value={m}>{SETUP_MODE_LABEL[m]}</option>
                  ))}
                </SelectField>
                <div>
                  <div className="flex items-center gap-1 text-caption text-neutral-700">
                    Status
                    <InfoTip label="About the setup status">
                      Computed from what exists, and the companies list shows the same. It reads
                      Ready once the required steps are done; Finish setup completes it.
                    </InfoTip>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <StatusBadge tone={s.setupStatus === 'COMPLETED' ? 'covered' : s.setupStatus === 'READY' ? 'pending' : 'neutral'}>
                      {SETUP_STATUS_LABEL[s.setupStatus]}
                    </StatusBadge>
                  </div>
                  {s.missingRequired.length > 0 && (
                    <p className="mt-2 text-caption text-warning-600">Still required: {s.missingRequired.join(', ')}.</p>
                  )}
                </div>
              </div>
            </DetailSection>

            <DetailSection title="Workforce (optional)">
              {fullSetup ? <FactsForm status={s} onSave={update} busy={busy} /> : <p>Company profile and clinical setup are managed by the {BRAND.name} team.</p>}
            </DetailSection>

            {fullSetup && <DetailSection title="Employee IDs">
              <EmployeeIdPreference base={base} onChanged={reload} />
            </DetailSection>}
          </div>
        </div>
      )}

      {section === 'structure' && <Structure base={base} onChanged={reload} />}
      {section === 'users' && <Users base={base} companyName={s?.tenant.name} onChanged={reload} />}
      {section === 'medical' && (
        <Medical base={base} industry={industry} onChanged={reload} />
      )}
      {section === 'requests' && <Requests base={base} onChanged={reload} />}

      {activeDialog === 'profile' && s && (
        <ProfileDialog
          tenantId={tenantId}
          tenant={s.tenant}
          onClose={() => setActiveDialog(null)}
          onDone={() => { setActiveDialog(null); reload(); }}
        />
      )}
      {activeDialog === 'location' && s && (
        <LocationDialog
          tenantId={tenantId}
          tenant={s.tenant}
          onClose={() => setActiveDialog(null)}
          onDone={() => { setActiveDialog(null); reload(); }}
        />
      )}
    </div>
  );
}

/** A record's name, with its code under it for the import-minded. */
function NameCode({ name, code }: { name: string; code: string }) {
  return (
    <div className="min-w-0">
      <div className="text-body text-neutral-900">{name}</div>
      <div className="font-mono text-caption text-neutral-500">{code}</div>
    </div>
  );
}

/** An enum value as a reader would write it: IN_REVIEW → "In review". */
function sentence(v: string): string {
  const s = v.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const STEP_LINK = 'inline-flex min-h-6 items-center tap-area rounded px-1.5 text-caption text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 disabled:opacity-50';

/* ── Profile and Location Dialogs ──────────────────────────── */

function ProfileDialog({ tenantId, tenant, onClose, onDone }: {
  tenantId: string;
  tenant: OnboardingStatus['tenant'];
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: tenant.name ?? '',
    legalName: tenant.legalName ?? '',
    gstin: tenant.gstin ?? '',
    contactName: tenant.contactName ?? '',
    contactDesignation: tenant.contactDesignation ?? '',
    contactPhone: tenant.contactPhone ?? '',
    contactEmail: tenant.contactEmail ?? '',
    website: tenant.website ?? '',
  });
  const [industry, setIndustry] = useState<Industry | null>(() => industryFromRef(tenant.industry));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (form.name.trim().length < 2) e.name = 'Company name is required';
    if (!form.contactPhone?.trim() && !form.contactEmail?.trim()) {
      e.contactPhone = 'At least phone or email is required';
      e.contactEmail = 'At least phone or email is required';
    }
    if (form.contactPhone?.trim() && !/^[6-9]\d{9}$/.test(form.contactPhone.trim())) e.contactPhone = 'Enter a valid 10-digit mobile number';
    if (form.contactEmail?.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactEmail.trim())) e.contactEmail = 'Enter a valid email address';
    if (form.gstin?.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/.test(form.gstin.trim())) e.gstin = 'Enter a valid GSTIN';
    return e;
  }

  async function submit() {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Please fill in all required fields.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const nullable = (v: string) => v.trim() || null;
      await api.patch(`/tenants/${tenantId}`, {
        name: form.name.trim(),
        legalName: nullable(form.legalName),
        gstin: nullable(form.gstin),
        industry: industry?.name ?? null,
        industryId: industry?.id ?? null,
        contactName: nullable(form.contactName),
        contactDesignation: nullable(form.contactDesignation),
        contactPhone: nullable(form.contactPhone),
        contactEmail: nullable(form.contactEmail),
        website: nullable(form.website),
      });
      onDone();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Company profile" info="A company name and at least one contact method are required. The industry is optional; when set, it suggests health packages in the medical configuration." onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="Company name"
            value={form.name}
            required
            error={fieldErrors.name}
            onChange={(v) => setForm({ ...form, name: v })}
          />
          <TextField
            label="Legal name"
            value={form.legalName}
            error={fieldErrors.legalName}
            onChange={(v) => setForm({ ...form, legalName: v })}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="GSTIN"
            value={form.gstin}
            mono
            error={fieldErrors.gstin}
            onChange={(v) => setForm({ ...form, gstin: v.toUpperCase() })}
          />
          <IndustryCombobox
            label="Industry (optional)"
            value={industry}
            onChange={setIndustry}
            error={fieldErrors.industry}
            required
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="Contact person"
            value={form.contactName}
            error={fieldErrors.contactName}
            onChange={(v) => setForm({ ...form, contactName: v })}
          />
          <TextField
            label="Designation"
            value={form.contactDesignation}
            error={fieldErrors.contactDesignation}
            onChange={(v) => setForm({ ...form, contactDesignation: v })}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="Contact phone"
            value={form.contactPhone}
            mono
            error={fieldErrors.contactPhone}
            onChange={(v) => setForm({ ...form, contactPhone: v.replace(/\D/g, '').slice(0, 10) })}
          />
          <TextField
            label="Contact email"
            value={form.contactEmail}
            error={fieldErrors.contactEmail}
            onChange={(v) => setForm({ ...form, contactEmail: v.trim() })}
          />
        </div>

        <TextField
          label="Website"
          value={form.website}
          error={fieldErrors.website}
          onChange={(v) => setForm({ ...form, website: v.trim() })}
          placeholder="https://example.com"
        />
      </div>

      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Save profile</Button>
      </div>
    </Dialog>
  );
}

function LocationDialog({ tenantId, tenant, onClose, onDone }: {
  tenantId: string;
  tenant: OnboardingStatus['tenant'];
  onClose: () => void;
  onDone: () => void;
}) {
  const [address, setAddress] = useState<AddressFormValue>(() => addressForm({
    addressLine1: tenant.addressLine1 ?? null,
    addressLine2: tenant.addressLine2 ?? null,
    locality: tenant.locality ?? null,
    landmark: tenant.landmark ?? null,
    city: tenant.city ?? null,
    district: tenant.district ?? null,
    state: tenant.state ?? null,
    country: tenant.country ?? null,
    pincode: tenant.pincode ?? null,
    postOffice: tenant.postOffice ?? null,
    // Seeded, so the map opens on the saved pin instead of an empty one.
    latitude: tenant.latitude ?? null,
    longitude: tenant.longitude ?? null,
    placeId: tenant.placeId ?? null,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // The same rule as the Primary location step: the pincode, and the city
  // and state it fills. The street is optional, as at registration.
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!address.pincode || address.pincode.length !== 6) e.pincode = 'Enter a valid 6-digit pincode';
    else {
      if (!address.city?.trim() && !address.district?.trim()) e.city = 'Enter the city';
      if (!address.state?.trim()) e.state = 'Enter the state';
    }
    return e;
  }

  async function submit() {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Please fill in the required address fields.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await api.patch(`/tenants/${tenantId}`, addressPayload(address, { clearable: true }));
      onDone();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Primary registered address" onClose={onClose} wide>
      <AddressFields value={address} onChange={setAddress} errors={fieldErrors} pincodeRequired />
      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Save location</Button>
      </div>
    </Dialog>
  );
}

/* ── Facts ─────────────────────────────────────────────────── */

/** The company chose once at its first employee; support undoes a wrong choice here. */
function EmployeeIdPreference({ base, onChanged }: { base: string; onChanged: () => void }) {
  const preference = useQuery<{ mode: 'EXISTING' | 'AUTOMATIC' | null; prefix: string | null }>(() => api.get(`${base}/employee-ids`), [base]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = preference.data;
  return (
    <div className="space-y-2">
      {preference.error && <Alert tone="danger">{preference.error}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      <p className="text-sm text-neutral-700">
        {preference.loading ? 'Loading…' : !p?.mode ? 'Not chosen yet. HR chooses when adding or importing the first employee.'
          : p.mode === 'AUTOMATIC' ? `Generated automatically, prefix ${p.prefix}.` : 'The company uses its own employee IDs.'}
      </p>
      {p?.mode && (
        <Button size="sm" variant="secondary" loading={busy} onClick={async () => {
          if (!window.confirm('Let the company choose again? IDs already issued keep their prefix and numbering.')) return;
          setBusy(true); setError(null);
          try { await api.del(`${base}/employee-ids`); preference.reload(); onChanged(); }
          catch (e) { setError(errorMessage(e)); }
          finally { setBusy(false); }
        }}>Reset the choice</Button>
      )}
    </div>
  );
}

function FactsForm({ status: s, onSave, busy }: {
  status: OnboardingStatus; onSave: (patch: Record<string, unknown>) => Promise<void>; busy: boolean;
}) {
  const [band, setBand] = useState<string>(s.tenant.workforceBand ?? '');
  const [count, setCount] = useState(s.tenant.workforceCount != null ? String(s.tenant.workforceCount) : '');
  return (
    <div className="space-y-3">
      <SelectField label="How many employees?" value={band} onChange={setBand}>
        <option value="">Not known</option>
        {WORKFORCE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
      </SelectField>
      <TextField label="Exact number" value={count} inputMode="numeric"
        onChange={(v) => setCount(v.replace(/\D/g, ''))} />
      {s.suggestion && <p className="text-caption text-neutral-500">{s.suggestion.text}</p>}
      <div className="flex justify-end">
        <Button size="sm" loading={busy} onClick={() => onSave({
          workforceBand: (band || null) as WorkforceBand | null,
          workforceCount: count ? Number(count) : null,
        })}>Save</Button>
      </div>
    </div>
  );
}

/* ── Plants ─────────────────────────────────────────────────── */

function Structure({ base, onChanged }: { base: string; onChanged: () => void }) {
  const plants = useQuery<CursorPage<PlantDetail>>(() => api.get(`${base}/plants?limit=100`), [base]);
  // Designations left the employee record (docs/38); the ones already
  // recorded stay on the employees that carry them.
  const [dialog, setDialog] = useState<null | 'plant'>(null);
  const [editingPlant, setEditingPlant] = useState<PlantDetail | null>(null);
  const reload = () => { plants.reload(); onChanged(); };

  return (
    <div className="max-w-3xl">
      <DetailSection title={`Plants (${plants.data?.items.length ?? 0})`}
        action={<Button size="sm" onClick={() => setDialog('plant')}><Plus className="h-3.5 w-3.5" /> Add</Button>}>
        <DataTable<PlantDetail>
          rows={plants.data?.items ?? []} loading={plants.loading} error={plants.error} onRetry={plants.reload}
          rowKey={(p) => p.id}
          empty={{ title: 'No plants', hint: 'An employee belongs to a plant. Add the first one.' }}
          columns={[
            { key: 'name', header: 'Plant', alwaysVisible: true, cell: (p) => <NameCode name={p.name} code={p.code} /> },
            { key: 'where', header: 'Location', cell: (p) => <span className="text-small text-neutral-600">{[p.city, p.state].filter(Boolean).join(', ') || 'Not recorded'}</span> },
            { key: 'edit', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (p) => (
              <RowActions>
                <IconAction label={`Edit ${p.name}`} icon={Pencil} onClick={() => setEditingPlant(p)} />
              </RowActions>
            ) },
          ]}
        />
      </DetailSection>

      {editingPlant && <PlantDialog base={base} plant={editingPlant} onClose={() => setEditingPlant(null)} onDone={() => { setEditingPlant(null); reload(); }} />}
      {dialog === 'plant' && <PlantDialog base={base} onClose={() => setDialog(null)} onDone={() => { setDialog(null); reload(); }} />}
    </div>
  );
}

function PlantDialog({ base, plant, onClose, onDone }: { base: string; plant?: PlantDetail; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ code: plant?.code ?? '', name: plant?.name ?? '' });
  const [address, setAddress] = useState<AddressFormValue>(addressForm(plant ?? null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const idem = useIdempotencyKey();

  async function submit() {
    setBusy(true); setError(null); setFieldErrors({});
    try {
      const payload = { code: form.code.toUpperCase(), name: form.name.trim(), ...addressPayload(address) };
      if (plant) await api.patch(`${base}/plants/${plant.id}`, { ...payload, version: plant.version });
      else await api.post(`${base}/plants`, payload, idem.key());
      idem.rotate();
      onDone();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    // A plant needs a code, a name and where it is — the pincode fills the
    // city and state (owner's review, 14 Oct 2026: the full address form,
    // map and gate contact were too much for a plant).
    <Dialog title={plant ? 'Edit plant' : 'Add a plant'} onClose={onClose} wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Code" value={form.code} mono required error={fieldErrors.code} placeholder="PLANT-01"
          onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} hint="Matched against the Plant Code column on import." />
        <TextField label="Name" value={form.name} required error={fieldErrors.name}
          onChange={(v) => setForm({ ...form, name: v })} />
      </div>
      <div className="mt-3"><AddressFields value={address} onChange={setAddress} brief errors={fieldErrors} /></div>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={form.code.trim().length < 2 || form.name.trim().length < 2}>{plant ? 'Save changes' : 'Add plant'}</Button>
      </div>
    </Dialog>
  );
}

/* ── Users ─────────────────────────────────────────────────── */

interface SetupUser {
  id: string; email: string; fullName: string; status: string; lastLoginAt: string | null;
  mfaEnabled: boolean; roles: Array<{ key: string; name: string; scopeType: string; scopeIds: string[] }>;
}

const COMPANY_ROLES = [
  ['PLANT_HR', 'HR'],
  ['PLANT_ACCOUNTS', 'Accounts'],
  ['EHS_VIEWER', 'EHS'],
  ['HR_ADMIN', 'Company Admin'],
] as const;

/** The roles above that always work for a plant. */
const PLANT_ROLE_KEYS: ReadonlySet<string> = new Set(['PLANT_HR', 'PLANT_ACCOUNTS']);

function Users({ base, companyName, onChanged }: { base: string; companyName?: string; onChanged: () => void }) {
  const { permissions } = useAuth();
  const users = useQuery<CursorPage<SetupUser>>(() => api.get(`${base}/users`), [base]);
  const plants = useQuery<CursorPage<PlantDetail>>(() => api.get(`${base}/plants?limit=100`), [base]);
  const [adding, setAdding] = useState(false);
  const [sent, setSent] = useState<{ email: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canImpersonate = holds(permissions, 'platform.impersonate');
  const signIn = useSignInAs();

  /** Sends the emailed link; a second click resends and kills the first. */
  async function sendInvitation(u: SetupUser) {
    setError(null);
    try {
      const r = await api.post<{ email: string; expiresAt: string }>(`${base}/users/${u.id}/invite`);
      setSent({ email: r.email, expiresAt: r.expiresAt });
    } catch (e) { setError(errorMessage(e)); }
  }

  return (
    <DetailSection title={`Company users (${users.data?.items.length ?? 0})`}
      action={<Button size="sm" onClick={() => setAdding(true)} icon={<Plus aria-hidden className="h-3.5 w-3.5" />}>Invite</Button>}>
      {(error || signIn.error) && <Alert className="mb-2">{error ?? signIn.error}</Alert>}
      <DataTable<SetupUser>
        rows={users.data?.items ?? []} loading={users.loading} error={users.error} onRetry={users.reload}
        rowKey={(u) => u.id}
        empty={{ title: 'Nobody yet', hint: 'HR access pending. Invite an HR user when the company is ready.' }}
        columns={[
          { key: 'who', header: 'Person', alwaysVisible: true, cell: (u) => (
            <div className="min-w-0"><div className="text-body text-neutral-900">{u.fullName}</div><div className="truncate font-mono text-caption text-neutral-500">{u.email}</div></div>
          ) },
          { key: 'role', header: 'Role', cell: (u) => {
            // Which plant a plant-bound user works for; nothing more for a company-wide one.
            const ids = [...new Set(u.roles.filter((r) => r.scopeType === 'PLANT').flatMap((r) => r.scopeIds ?? []))];
            const codes = ids.map((id) => plants.data?.items.find((p) => p.id === id)?.code ?? 'another plant');
            return (
              <div>
                <span className="text-small text-neutral-700">{u.roles.map((r) => r.name).join(', ') || '—'}</span>
                <div className="text-caption text-neutral-500">{codes.length ? `Plant: ${codes.join(', ')}` : 'All plants'}</div>
              </div>
            );
          } },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (u) => (
            <StatusBadge tone={u.status === 'ACTIVE' ? 'covered' : u.status === 'INVITED' ? 'pending' : 'overdue'}>
              {u.status === 'INVITED' ? 'Invited' : USER_STATUS_LABEL[u.status as keyof typeof USER_STATUS_LABEL] ?? u.status}
            </StatusBadge>
          ) },
          { key: 'login', header: 'Last sign-in', hideOnMobile: true, cell: (u) => <span className="text-small text-neutral-600">{u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}</span> },
          { key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (u) => (
            <RowActions>
              {u.status === 'INVITED' && (
                <IconAction label={`Send the invitation to ${u.fullName}`} icon={Send} onClick={() => sendInvitation(u)} />
              )}
              {/* One click, full access, recorded server-side (owner, 29 Oct 2026). */}
              {u.status === 'ACTIVE' && canImpersonate && (
                <SignInAsAction name={u.fullName} path={`/admin/users/${u.id}/impersonate`} signIn={signIn} />
              )}
            </RowActions>
          ) },
        ]}
      />
      {adding && (
        <InviteDialog base={base} plants={plants.data?.items ?? []} onClose={() => setAdding(false)}
          onDone={(invitation) => {
            setAdding(false); users.reload(); onChanged();
            if (invitation) setSent(invitation);
            else setError('The account was created but the email could not be sent. Use Send invitation on their row.');
          }} />
      )}
      {sent && (
        <Dialog title="Invitation sent" onClose={() => setSent(null)}>
          <p className="mt-2 text-sm text-neutral-700">
            A link to choose a password is on its way to <span className="font-mono">{sent.email}</span>.
            It works once, until {formatDateTime(sent.expiresAt)}.
            Sending again replaces it.
          </p>
          <div className="mt-5 flex justify-end"><Button onClick={() => setSent(null)}>Done</Button></div>
        </Dialog>
      )}
    </DetailSection>
  );
}

function InviteDialog({ base, plants, onClose, onDone }: {
  base: string; plants: Plant[]; onClose: () => void;
  /** The invitation that went out, or null when only the account was made. */
  onDone: (invitation: { email: string; expiresAt: string } | null) => void;
}) {
  const [form, setForm] = useState({ fullName: '', email: '', roleKey: 'PLANT_HR' });
  const modeOf = (key: string) => plantAccessMode({ key, scopeType: PLANT_ROLE_KEYS.has(key) ? 'PLANT' : 'TENANT' });
  const mode = modeOf(form.roleKey);
  const plantIds = plants.map((p) => p.id);
  const [plantAccess, setPlantAccess] = useState(() => initialPlantAccess(mode, plantIds));
  const scopeIds = scopeIdsFor(mode, plantAccess);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const idem = useIdempotencyKey();

  async function submit() {
    setBusy(true); setError(null); setFieldErrors({});
    try {
      const created = await api.post<{ id: string }>(`${base}/users`, {
        fullName: form.fullName.trim(), email: form.email.trim().toLowerCase(), roleKey: form.roleKey,
        ...(scopeIds.length ? { scopeIds } : {}),
      }, idem.key());
      idem.rotate();
      // The email goes out with the account, as in the HR console. If it
      // fails the account still exists and the row offers Send invitation.
      let invitation: { email: string; expiresAt: string } | null = null;
      try {
        invitation = await api.post<{ email: string; expiresAt: string }>(`${base}/users/${created.id}/invite`);
      } catch { /* reported by the caller */ }
      onDone(invitation);
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title="Invite a company user" info="They get an email with a one-time link to choose their own password. Nobody here ever sees it." onClose={onClose}>
      <div className="mt-3 space-y-3">
        <TextField label="Full name" value={form.fullName} autoFocus error={fieldErrors.fullName} onChange={(v) => setForm({ ...form, fullName: v })} />
        <TextField label="Work email" value={form.email} mono error={fieldErrors.email} onChange={(v) => setForm({ ...form, email: v })} />
        <SelectField label="Role" value={form.roleKey} onChange={(v) => { setForm({ ...form, roleKey: v }); setPlantAccess(initialPlantAccess(modeOf(v), plantIds)); }}>
          {COMPANY_ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </SelectField>
        <PlantAccessField mode={mode} plants={plants} value={plantAccess} onChange={setPlantAccess} />
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}
          disabled={!form.email.includes('@') || form.fullName.trim().length < 2 || !plantAccessReady(mode, plantAccess)}>
          Invite
        </Button>
      </div>
    </Dialog>
  );
}

/* ── Medical configuration ─────────────────────────────────── */

function Medical({ base, industry, onChanged }: {
  base: string; industry: { id: string; code: string; name: string } | null; onChanged: () => void;
}) {
  const paths = { configs: `${base}/medical-configurations` };
  const configs = useQuery<CursorPage<MedicalConfigurationRow>>(() => api.get(paths.configs), [paths.configs]);
  const [building, setBuilding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<MedicalConfigurationRow | null>(null);
  const [automaticBusy, setAutomaticBusy] = useState(false);
  const [automaticMessage, setAutomaticMessage] = useState<string | null>(null);
  async function assignBaseline() {
    setAutomaticBusy(true); setAutomaticMessage(null);
    try {
      const result = await api.post<{ status: string; reason?: string }>(`${base}/automatic`, {});
      setAutomaticMessage(result.status === 'CONFIGURED' ? 'Approved medical baseline assigned.' : result.reason ?? 'Clinical approval required.');
      changed();
    } catch (error) { setAutomaticMessage(errorMessage(error)); }
    finally { setAutomaticBusy(false); }
  }
  // Every save and publish refreshes the setup status at once, so Progress
  // never says "nothing published" over a configuration in force (flow
  // audit 2.5) — including when somebody switches tabs without pressing Back.
  const changed = () => { configs.reload(); onChanged(); };

  if (building) {
    return <Builder paths={paths} industryId={industry?.id ?? null} canPublish={false} onCancel={() => setBuilding(false)}
      onSaved={() => { setBuilding(false); changed(); }} />;
  }
  if (openId) {
    return <Detail id={openId} paths={paths} canPublish={false} onPublished={onChanged} onBack={() => { setOpenId(null); changed(); }} />;
  }

  return (
    <DetailSection title="Medical configuration"
      action={<Button size="sm" onClick={() => setBuilding(true)}><Plus className="h-3.5 w-3.5" /> New configuration</Button>}>
      <div className="mb-3 space-y-2">
        <p>New companies receive the centrally approved standard automatically. If approval was pending during registration, assign it now.</p>
        <Button size="sm" variant="secondary" loading={automaticBusy} onClick={assignBaseline}>Assign approved standard</Button>
        {automaticMessage && <p role="status">{automaticMessage}</p>}
      </div>
      <div className="mb-1 flex justify-end">
        <InfoTip label="About the medical configuration">
          Prepared here on the company’s behalf, for all employees.
          HR sees it read-only and asks for changes in writing.
        </InfoTip>
      </div>
      <DataTable<MedicalConfigurationRow>
        rows={configs.data?.items ?? []} loading={configs.loading} error={configs.error} onRetry={configs.reload}
        rowKey={(c) => c.id} onRowClick={(c) => setOpenId(c.id)}
        empty={{ title: 'Nothing configured yet', hint: 'Assign the approved standard. If none exists, the clinical team must approve a baseline first.' }}
        columns={[
          { key: 'name', header: 'Configuration', cell: (c) => (
            <div><div className="text-body text-neutral-900">{c.name}</div><div className="text-caption text-neutral-500">{sentence(String(c.scope))}</div></div>
          ) },
          { key: 'packages', header: 'Packages', align: 'right', cell: (c) => <span className="text-small tabular-nums">{c.packageCount}</span> },
          { key: 'status', header: 'State', cell: (c) => (
            <div>
              <StatusBadge tone={c.status === 'PUBLISHED' ? 'covered' : c.status === 'DRAFT' ? 'pending' : 'neutral'}>
                {c.status === 'PUBLISHED' ? `In force · v${c.configVersion}` : sentence(c.status)}
              </StatusBadge>
              {c.status === 'DRAFT' && <div className="mt-0.5 text-caption text-neutral-500">Clinical approval required</div>}
            </div>
          ) },
          { key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (c) => (
            <RowActions>
              <IconAction label={`View ${c.name}`} icon={Eye} onClick={() => setOpenId(c.id)} />
            </RowActions>
          ) },
        ]}
      />
      {publishing && (
        <ConfirmDialog
          title={`Publish ${publishing.name}?`}
          what={`It comes into force for ${publishing.departmentName ? `nobody — departments no longer apply` : publishing.jobRole ? `the ${publishing.jobRole.title} role` : 'every employee'}. Examinations raised from now on use its tests.`}
          affects="Examinations already raised keep the tests they were created with."
          irreversible="A published configuration cannot be edited. Whatever was in force for the same scope is superseded and kept for history."
          confirmLabel="Publish"
          onConfirm={async () => {
            await api.post(`${paths.configs}/${publishing.id}/publish`, {});
            setPublishing(null);
            changed();
          }}
          onCancel={() => setPublishing(null)}
        />
      )}
    </DetailSection>
  );
}

/* ── Change requests ───────────────────────────────────────── */

function Requests({ base, onChanged }: { base: string; onChanged: () => void }) {
  const requests = useQuery<CursorPage<MedicalConfigChangeRequest>>(() => api.get(`${base}/change-requests?status=ALL`), [base]);
  const [resolving, setResolving] = useState<MedicalConfigChangeRequest | null>(null);

  return (
    <DetailSection title="Change requests from HR">
      <DataTable<MedicalConfigChangeRequest>
        rows={requests.data?.items ?? []} loading={requests.loading} error={requests.error} onRetry={requests.reload}
        rowKey={(r) => r.id}
        empty={{ title: 'No requests', hint: 'HR can ask for changes to the medical configuration from their console.' }}
        columns={[
          { key: 'subject', header: 'Request', cell: (r) => (
            <div><div className="text-body text-neutral-900">{r.subject}</div><div className="text-caption text-neutral-500">{r.message}</div>
              {r.response && <div className="mt-1 text-caption text-neutral-700"><span className="font-medium">Answer:</span> {r.response}</div>}</div>
          ) },
          { key: 'config', header: 'About', cell: (r) => <span className="text-small text-neutral-600">{r.config?.name ?? 'General'}</span> },
          { key: 'when', header: 'Raised', cell: (r) => <span className="text-small text-neutral-600">{formatDate(r.createdAt)}</span> },
          { key: 'status', header: 'Status', cell: (r) => (
            <StatusBadge tone={r.status === 'RESOLVED' ? 'covered' : r.status === 'REJECTED' ? 'overdue' : 'pending'}>{sentence(r.status)}</StatusBadge>
          ) },
          { key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (r) => r.status === 'OPEN' || r.status === 'IN_REVIEW' ? (
            <RowActions>
              <IconAction label={`Answer the request “${r.subject}”`} icon={MessageSquareReply} primary onClick={() => setResolving(r)} />
            </RowActions>
          ) : null },
        ]}
      />
      {resolving && (
        <ResolveDialog base={base} request={resolving} onClose={() => setResolving(null)}
          onDone={() => { setResolving(null); requests.reload(); onChanged(); }} />
      )}
    </DetailSection>
  );
}

function ResolveDialog({ base, request, onClose, onDone }: {
  base: string; request: MedicalConfigChangeRequest; onClose: () => void; onDone: () => void;
}) {
  const [status, setStatus] = useState<'IN_REVIEW' | 'RESOLVED' | 'REJECTED'>('RESOLVED');
  const [response, setResponse] = useState(request.response ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`${base}/change-requests/${request.id}/resolve`, { status, response: response.trim() || undefined });
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog title={request.subject} onClose={onClose}>
      <p className="mt-1 text-sm text-neutral-700">{request.message}</p>
      <div className="mt-3 space-y-3">
        <Field label="Outcome">
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as never)}>
              <option value="IN_REVIEW">In review — keep open</option>
              <option value="RESOLVED">Resolved — the configuration was updated</option>
              <option value="REJECTED">Not done — explain why</option>
            </Select>
          )}
        </Field>
        <Field label="Answer to HR" hint="Shown beside the request in their console.">
          {(p) => <Textarea {...p} value={response} onChange={(e) => setResponse(e.target.value)} rows={4} />}
        </Field>
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Save</Button>
      </div>
    </Dialog>
  );
}
